import React, { useId } from 'react';
import { AlertTriangle } from 'lucide-react';
import classes from '../styles.module.scss';
import { createCn } from '../../../helpers/utils';
import type { VideoScreenInsightSection } from '../../../services/api.service';
import VideoInsightsBarChart from './video-insights-bar-chart.component';
import VideoInsightsAccordion from './video-insights-accordion.component';
import {
  buildChartData,
  buildSourceMetaLine,
  detectSuspiciousCells,
  formatCellValue,
  isDeviationColumn,
  isLabelColumn,
  isNumericColumn,
  isTotalRow,
  deviationTone,
  sectionAnchorId,
} from '../utils/video-insights-utils';

const cn = createCn('file', classes);

type Props = {
  section: VideoScreenInsightSection;
  index: number;
  defaultOpen?: boolean;
};

const VideoInsightsSourceTable = ({ section, index, defaultOpen = false }: Props) => {
  const tableId = sectionAnchorId(index, section.title);
  const columns = section.columns || [];
  const rows = section.rows || [];
  const suspicious = detectSuspiciousCells(rows, columns);
  const chart = buildChartData(section);
  const hintId = useId();

  if (!columns.length || !rows.length) return null;

  return (
    <VideoInsightsAccordion
      id={tableId}
      title={section.title}
      subtitle={buildSourceMetaLine(section)}
      defaultOpen={defaultOpen}
      variant="table"
    >
      <div className={cn('vi-table-body', { 'with-chart': !!chart })}>
        {chart && (
          <VideoInsightsBarChart
            title={chart.column}
            items={chart.items}
          />
        )}

        <div className={cn('vi-table-wrap')}>
          <table className={cn('vi-table')}>
            <thead>
              <tr>
                {columns.map(col => (
                  <th
                    key={col}
                    className={cn('vi-table-th', {
                      numeric: isNumericColumn(col, rows),
                      label: isLabelColumn(col),
                    })}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => {
                const isTotal = isTotalRow(row, columns);
                return (
                  <tr key={rIdx} className={cn('vi-table-tr', { total: isTotal })}>
                    {columns.map(col => {
                      const raw = row[col];
                      const display = formatCellValue(raw);
                      const isSuspicious = suspicious.has(`${rIdx}:${col}`);
                      const isDev = isDeviationColumn(col);
                      const tone = isDev ? deviationTone(display, col) : undefined;
                      const isEmpty = display === '—';

                      return (
                        <td
                          key={col}
                          className={cn('vi-table-td', {
                            numeric: isNumericColumn(col, rows),
                            label: isLabelColumn(col),
                            empty: isEmpty,
                            suspicious: isSuspicious,
                            positive: tone === 'positive',
                            negative: tone === 'negative',
                            neutral: tone === 'neutral' && isDev,
                          })}
                        >
                          <span
                            className={cn('vi-table-cell-inner', {
                              label: isLabelColumn(col),
                            })}
                          >
                            {isSuspicious && (
                              <AlertTriangle
                                size={11}
                                className={cn('vi-table-warn-icon')}
                                aria-label="Проверьте по кадру"
                                aria-describedby={hintId}
                              />
                            )}
                            {display}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {section.notes && (
        <p className={cn('vi-table-notes')}>{section.notes}</p>
      )}
      {suspicious.size > 0 && (
        <p id={hintId} className={cn('vi-table-ocr-hint')}>
          Ячейки с иконкой — возможная ошибка OCR; сверьте с кадром видео.
        </p>
      )}
    </VideoInsightsAccordion>
  );
};

export default VideoInsightsSourceTable;
